import { z } from 'zod'
import { wordlist } from '@scure/bip39/wordlists/english'
import * as bip39 from '@scure/bip39'
import { HDKey } from '@scure/bip32'
import { toBuffer } from '@ethereumjs/util'

import { Wallet } from '@/business-logic/wallet/domain/wallet/Wallet'
import { PrivateKey } from '@/business-logic/wallet/domain/wallet/PrivateKey'
import { Account } from '@/business-logic/wallet/domain/wallet/Account'

const HDWalletOptionsSchema = z.object({
  mnemonic: z.record(z.number()),
  childIndices: z.array(z.number()),
  hdPath: z.string(),
  networkIds: z.array(z.number()),
  seedId: z.union([z.string(), z.number()]),
})

const SimpleWalletOptionsSchema = z.object({
  privateKeys: z.array(z.string()),
  networkIds: z.array(z.number()),
})

const WalletOptionsSchema = z.union([
  HDWalletOptionsSchema,
  SimpleWalletOptionsSchema,
])

const SerializedWalletSchema = z.object({
  type: z.union([z.literal('HD Wallet'), z.literal('Simple Wallet')]),
  data: WalletOptionsSchema,
})

const WalletSchemaV1Schema = z.array(SerializedWalletSchema)

type WalletSchemaV1 = z.infer<typeof WalletSchemaV1Schema>

/**
 * Checks to see if the wallet is a V1 wallet.
 */
export function isV1Wallet(walletDTO: unknown): walletDTO is WalletSchemaV1 {
  const result = WalletSchemaV1Schema.safeParse(walletDTO)
  return result.success
}

function parseSimpleWallet(data: unknown): string[] {
  const simpleWalletOptions = SimpleWalletOptionsSchema.parse(data)
  return simpleWalletOptions.privateKeys
}

function uint8ArrayToMnemonicString(mnemonicData: Uint8Array) {
  const recoveredIndices = [
    ...new Uint16Array(new Uint8Array(mnemonicData).buffer),
  ]
  return recoveredIndices.map(i => wordlist[i]).join(' ')
}

function initFromMnemonic(
  rawMnemonic: Record<string, number>,
  hdPath: string
): HDKey {
  const mnemonic = Uint8Array.from(Object.values(rawMnemonic))
  const stringMnemonic = uint8ArrayToMnemonicString(mnemonic)
  if (!bip39.validateMnemonic(stringMnemonic, wordlist)) {
    throw new Error('HD Wallet: Invalid secret recovery phrase provided')
  }
  const seed = bip39.mnemonicToSeedSync(stringMnemonic)
  const hdWallet = HDKey.fromMasterSeed(seed)
  return hdWallet.derive(hdPath)
}

function deriveAccount(root: HDKey, index: number): string {
  const child = root.deriveChild(index)
  if (!child.privateKey) {
    throw new Error('HD Wallet: No private key')
  }
  return toBuffer(child.privateKey).toString('hex')
}

function parseHDWallet(data: unknown): string[] {
  const hdWalletOptions = HDWalletOptionsSchema.parse(data)

  const { mnemonic, hdPath, childIndices } = hdWalletOptions
  const root = initFromMnemonic(mnemonic, hdPath)

  return childIndices.map(childIndex => deriveAccount(root, childIndex))
}

/**
 * Maps V1 vault data to a Wallet.
 * @param vault V1 vault data
 * @returns Wallet
 */
export function mapDTOToDomain(v1VaultData: unknown): Wallet {
  const parsedVault = WalletSchemaV1Schema.parse(v1VaultData)

  const privateKeyValues: Set<string> = new Set()
  for (const serializedWallet of parsedVault) {
    const newPrivateKeys =
      serializedWallet.type === 'Simple Wallet'
        ? parseSimpleWallet(serializedWallet.data)
        : parseHDWallet(serializedWallet.data)
    for (const newPrivateKey of newPrivateKeys) {
      const normalizedPrivateKey = newPrivateKey.toLowerCase()
      privateKeyValues.add(normalizedPrivateKey)
    }
  }

  const accounts = [...privateKeyValues].map(privateKeyValue =>
    Account.create({
      privateKey: PrivateKey.create({
        privateKeyValue: Buffer.from(privateKeyValue, 'hex'),
      }),
    })
  )

  return Wallet.create({ accounts })
}
